import { Box, Button, Tooltip } from '@modusign/design-system';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';

import clipIcon from '@app/assets/icons/icon-clip-18h.svg';
import useMemberPermissions from '@app/hooks/useMemberPermission';
import { downloadDocumentFile } from '@app/store/document/file/actions';
import { getRequesterAttachments } from '@app/store/documentSetup/selectors';
import { modalClose } from '@app/store/modal/actions';
import {
  getDocument,
  getSigningProgressSignerAttachments,
} from '@app/store/signingProgressInformation/selectors';

import './SignatureProgressInformationFooter.scss';

interface SignatureProgressInformationFooterProps {
  documentId: string;
  onOpenAttachmentsModal: () => void;
}

function SignatureProgressInformationFooter({
  documentId,
  onOpenAttachmentsModal,
}: SignatureProgressInformationFooterProps) {
  const history = useHistory();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const { isPermitted: ableToDownload } = useMemberPermissions('EXPORT_DOCUMENT');

  const signerAttachments = useSelector(getSigningProgressSignerAttachments);
  const document = useSelector(getDocument);
  const requesterAttachments = useSelector(getRequesterAttachments);

  const hasAttachments = signerAttachments.length > 0 || requesterAttachments.length > 0;

  const handleDownloadFile = useCallback(() => {
    if (documentId) {
      dispatch(
        downloadDocumentFile({
          documentId,
          documentTitle: document.name,
        })
      );
    }
  }, [dispatch, document.name, documentId]);

  const goToAuditTrail = useCallback(() => {
    if (documentId) {
      history.push({
        pathname: '/document/audit-trail',
        state: {
          documentId,
        },
      });
    }
    dispatch(modalClose());
  }, [dispatch, documentId, history]);

  return (
    <div className="WindowModal__footer align-right align-center-mobile">
      <Tooltip content="권한 없음" disabled={ableToDownload}>
        <Box
          as="button"
          className="SignatureProgressInformationFooter__btn SignatureProgressInformationFooter__btn--half Btn Btn--light-mobile"
          data-name="document-download-btn"
          disabled={!ableToDownload}
          type="button"
          onClick={handleDownloadFile}
        >
          {t('SignatureProgressInformationFooter-documentDownloadButton')}
        </Box>
      </Tooltip>
      {hasAttachments && (
        <button
          className="SignatureProgressInformationFooter__btn SignatureProgressInformationFooter__btn--half Btn Btn--light-mobile"
          data-name="document-download-btn"
          type="button"
          onClick={onOpenAttachmentsModal}
        >
          <img
            alt="clip-icon"
            className="SignatureProgressInformationFooter__clip-icon"
            src={clipIcon}
          />
          {t('SignatureProgressInformationFooter-showAttachmentButton')}
        </button>
      )}
      <Button
        className="SignatureProgressInformationFooter__btn Btn Btn--light-mobile Btn--primary Btn--full-mobile"
        type="button"
        onClick={goToAuditTrail}
      >
        {t('SignatureProgressInformationFooter-viewCertificationButton')}
      </Button>
    </div>
  );
}

export default SignatureProgressInformationFooter;
